import { Menu, MenuButton, MenuItem, MenuList, MenuSubMenu } from '@/src/components/ui/Menu'
import { Button, Divider, Flex, Image, Text, useDisclosure } from '@chakra-ui/react'
import { Avatar, FlexCol, FlexRow, SimpleConfirmModal } from '@components'
import { useTranslation } from 'react-i18next'
import { RxCaretDown } from 'react-icons/rx'
import { useNavigate } from 'react-router-dom'

import { useProfilePicture } from '@/src/hooks/useProfilePicture.hook'
import { useSessionStore } from '@/src/hooks/use_session_store'
import { getSupportedLanguage } from '@/src/shared/application/translation.service'

const LANGUAGES = [{ code: 'de' }, { code: 'en' }] as const

export const UserMenu = () => {
  const { user } = useSessionStore()
  const navigate = useNavigate()
  const confirmLogout = useDisclosure()
  const { t, i18n } = useTranslation()
  const selectedLanguage = getSupportedLanguage(i18n.language)
  const session = useSessionStore()
  const languages = LANGUAGES.map(language => ({
    ...language,
    label: t(`user_menu.languages.${language.code}`),
  }))

  const { data: profilePicUrl = '' } = useProfilePicture()

  if (!user) {
    return null
  }

  const handleLogout = () => {
    session.logout()
  }

  const handleLanguageChange = (languageCode: string) => {
    void i18n.changeLanguage(languageCode)
  }

  const userFullName = `${user.firstName} ${user.lastName}`

  return (
    <>
      <Menu placement='bottom-end'>
        <MenuButton paddingLeft={'0px'} as={Button} rightIcon={<RxCaretDown size={24} />} background='transparent' border='none'>
          <Flex paddingRight={2} paddingY={2} justifyContent='space-between' h='full'>
            <FlexRow alignItems={'center'} cursor={'pointer'}>
              {profilePicUrl !== '' ? (
                <Image boxSize='100px' borderRadius='full' objectFit='cover' src={profilePicUrl} w='32px' h='32px' />
              ) : (
                <Avatar fontSize='14px' name={userFullName} width='32px' height='32px' />
              )}
              <FlexCol ml='12px'>
                <Text fontSize={'14px'} noOfLines={1}>{`${user.firstName} ${user.lastName}`}</Text>
              </FlexCol>
            </FlexRow>
          </Flex>
        </MenuButton>
        <MenuList zIndex={10}>
          <MenuItem
            class='standard'
            onClick={() => {
              navigate('/profile/personal-info')
            }}
          >
            <Text>{t('user_menu.settings')}</Text>
          </MenuItem>

          <MenuSubMenu label={`${t('user_menu.language')}: ${languages.find(l => l.code === selectedLanguage)?.label ?? selectedLanguage}`}>
            {languages.map(lang => (
              <MenuItem
                key={lang.code}
                class='standard'
                isSelected={selectedLanguage === lang.code}
                onClick={() => handleLanguageChange(lang.code)}
              >
                <Text>{lang.label}</Text>
              </MenuItem>
            ))}
          </MenuSubMenu>

          <Divider my='4px' />
          <MenuItem class='standard' onClick={confirmLogout.onOpen}>
            <Text>{t('user_menu.logout')}</Text>
          </MenuItem>
        </MenuList>
      </Menu>

      <SimpleConfirmModal
        title={t('user_menu.logout_confirm_title')}
        acceptLabel={t('user_menu.logout_confirm_accept')}
        cancelLabel={t('user_menu.logout_confirm_cancel')}
        bodyText={t('user_menu.logout_confirm_body')}
        isOpen={confirmLogout.isOpen}
        onClose={confirmLogout.onClose}
        onAccept={handleLogout}
      />
    </>
  )
}
